/**
 * Text-to-Speech Service
 * Provides voice feedback to user
 */

class TextToSpeechService {
  private synth: SpeechSynthesis | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  private selectedVoice: SpeechSynthesisVoice | null = null;
  private isSpeaking = false;
  private rate = 1.0;
  private pitch = 1.0;
  private volume = 1.0;

  constructor() {
    if ('speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
      this.loadVoices();

      // Chrome loads voices asynchronously
      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = () => {
          this.loadVoices();
        };
      }
    }
  }

  private loadVoices() {
    if (!this.synth) return;

    this.voices = this.synth.getVoices();
    
    // Try to find a good English voice
    const preferredVoices = [
      'Samantha', // macOS
      'Google US English', // Chrome
      'Microsoft Zira', // Windows
      'Karen', // iOS
    ];

    for (const preferred of preferredVoices) {
      const voice = this.voices.find(v => v.name.includes(preferred));
      if (voice) {
        this.selectedVoice = voice;
        break;
      }
    }

    // Fallback to first English voice
    if (!this.selectedVoice) {
      this.selectedVoice = this.voices.find(v => v.lang.startsWith('en')) || this.voices[0];
    }
  }

  speak(text: string, options?: {
    rate?: number;
    pitch?: number;
    volume?: number;
    onEnd?: () => void;
    onError?: (error: any) => void;
  }): void {
    if (!this.synth) {
      return;
    }

    // Cancel any ongoing speech
    this.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    if (this.selectedVoice) {
      utterance.voice = this.selectedVoice;
    }

    utterance.rate = options?.rate ?? this.rate;
    utterance.pitch = options?.pitch ?? this.pitch;
    utterance.volume = options?.volume ?? this.volume;

    utterance.onstart = () => {
      this.isSpeaking = true;
    };

    utterance.onend = () => {
      this.isSpeaking = false;
      options?.onEnd?.();
    };

    utterance.onerror = (event) => {
      this.isSpeaking = false;
      console.error('Speech synthesis error:', event);
      options?.onError?.(event);
    };

    this.synth.speak(utterance);
  }

  cancel(): void {
    if (!this.synth) return;
    this.synth.cancel();
    this.isSpeaking = false;
  }

  pause(): void {
    if (!this.synth) return;
    this.synth.pause();
  }

  resume(): void {
    if (!this.synth) return;
    this.synth.resume();
  }

  setRate(rate: number): void {
    this.rate = Math.max(0.1, Math.min(10, rate));
  }

  setPitch(pitch: number): void {
    this.pitch = Math.max(0, Math.min(2, pitch));
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  setVoice(voiceName: string): void {
    const voice = this.voices.find(v => v.name === voiceName);
    if (voice) {
      this.selectedVoice = voice;
    }
  }

  getVoices(): SpeechSynthesisVoice[] {
    return this.voices;
  }

  isSupported(): boolean {
    return !!this.synth;
  }

  getIsSpeaking(): boolean {
    return this.isSpeaking;
  }

  // Preset responses for common scenarios
  speakSuccess(message: string): void {
    this.speak(`✓ ${message}`, { pitch: 1.1 });
  }

  speakError(message: string): void {
    this.speak(`✗ ${message}`, { pitch: 0.9 });
  }

  speakInfo(message: string): void {
    this.speak(message);
  }

  // Command-specific responses
  speakNavigationConfirm(location: string): void {
    this.speak(`Navigating to ${location}`);
  }

  speakDataFound(count: number, type: string): void {
    if (count === 0) {
      this.speak(`No ${type} found`);
    } else if (count === 1) {
      this.speak(`Found 1 ${type}`);
    } else {
      this.speak(`Found ${count} ${type}`);
    }
  }

  speakCommandNotUnderstood(): void {
    this.speak("I didn't understand that. Try saying 'help' to see what I can do.", { pitch: 0.95 });
  }

  speakHelp(): void {
    this.speak(
      "I can help you navigate, search for wells, create tasks, and more. " +
      "Try saying things like: 'Navigate to Well 042', 'Show me wells with issues', or 'What's my schedule today?'"
    );
  }
}

export const textToSpeech = new TextToSpeechService();
